import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { useDateRanges } from '../../../shared/lib/hooks/UseDateRanges'
import { AdvertiserReportFilterInterface } from './types/AdvertiserReportFilter.interface'
import { RootState } from '../../../app/store/Store'
import { IPagination } from '../../../shared/types/Pagination.interface'
import { IOption } from '../../../shared/components/forms/search-input/BpSearchInput'

type AdvertiserReportState = {
  filters: AdvertiserReportFilterInterface
  pagination: IPagination
  allowDecloak: boolean
  tabCount?: { [key: string]: string | number }
  project: IOption & {
    is_demo: boolean
    has_paid_links: boolean
    has_seo_links: boolean
    has_shopping_ads: boolean
  }
  country?: IOption & {
    keywords_state?: 'active' | 'deleted' | 'never'
  }
  isExpandAll: boolean
}

const dateRanges = useDateRanges()

const paidResultsSlice = createSlice({
  name: 'research',
  initialState: {
    filters: {
      dateRange: dateRanges.month,
      show_hidden: false,
      show_shopping_ads: false,
      is_decloak: false,
      policy: '',
      reversed_filters: '',
    },
    pagination: {
      current: 1,
      pageSize: 10,
      total: 1,
    },
    project: {
      is_demo: false,
      has_paid_links: true,
      has_seo_links: true,
      has_shopping_ads: false,
    },
    allowDecloak: false,
    isExpandAll: false,
  } as AdvertiserReportState,
  reducers: {
    setAdvertiserReportFilter: (
      state: AdvertiserReportState,
      action: PayloadAction<AdvertiserReportFilterInterface>,
    ) => {
      state.filters = { ...state.filters, ...action.payload }
    },
    setAdvertiserReportPagination: (
      state: AdvertiserReportState,
      action: PayloadAction<IPagination>,
    ) => {
      state.pagination = action.payload
    },
    setAllowDecloak: (state: AdvertiserReportState, action: PayloadAction<boolean>) => {
      state.allowDecloak = action.payload
    },
    setTabCount: (
      state: AdvertiserReportState,
      action: PayloadAction<{ [key: string]: string | number }>,
    ) => {
      state.tabCount = action.payload
    },
    setProject: (
      state: AdvertiserReportState,
      action: PayloadAction<
        IOption & {
          is_demo: boolean
          has_paid_links: boolean
          has_seo_links: boolean
          has_shopping_ads: boolean
        }
      >,
    ) => {
      state.project = action.payload
    },
    setCountry: (
      state: AdvertiserReportState,
      action: PayloadAction<
        | (IOption & {
            keywords_state?: 'active' | 'deleted' | 'never'
          })
        | undefined
      >,
    ) => {
      state.country = action.payload
    },
    toggleExpand: (state: AdvertiserReportState) => {
      state.isExpandAll = !state.isExpandAll
    },
  },
})

export const {
  setAdvertiserReportFilter,
  setAdvertiserReportPagination,
  setAllowDecloak,
  setTabCount,
  setProject,
  setCountry,
  toggleExpand,
} = paidResultsSlice.actions

export const selectAdvertiserReportFilters = (state: RootState) =>
  state.root.advertiserReport.filters
export const selectAdvertiserReportPagination = (state: RootState) =>
  state.root.advertiserReport.pagination
export const selectAdvertiserReportTabCount = (state: RootState) =>
  state.root.advertiserReport.tabCount
export const selectAllowDecloak = (state: RootState) => state.root.advertiserReport.allowDecloak
export const selectProject = (state: RootState) => state.root.advertiserReport.project
export const selectCountry = (state: RootState) => state.root.advertiserReport.country
export const selectIsExpandAll = (state: RootState) => state.root.advertiserReport.isExpandAll

export default paidResultsSlice.reducer
